
import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Calendar, Users, BookOpen, Globe, Award, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

type ArticleType = {
  id: number;
  title: string;
  summary: string;
  date: string;
  category: "educational" | "events" | "exhibitions" | "studies" | "local" | "regional" | "global";
  imageUrl: string;
};

const articlesData: ArticleType[] = [
  {
    id: 1,
    title: "أحدث التقنيات في صناعة الألمنيوم",
    summary: "تعرف على أحدث التقنيات والابتكارات في مجال صناعة الألمنيوم وتطبيقاتها في الديكورات المعاصرة",
    date: "2023-10-15",
    category: "educational",
    imageUrl: "/lovable-uploads/961c902e-6f31-4626-9dff-6afe3848e22b.png"
  },
  {
    id: 2,
    title: "معرض البناء السعودي 2023",
    summary: "تغطية شاملة لمعرض البناء السعودي وأبرز المشاركين والابتكارات المعروضة",
    date: "2023-09-22",
    category: "exhibitions",
    imageUrl: "/lovable-uploads/fd7f9037-362e-47cd-868b-d7dca218fa60.png"
  },
  {
    id: 3,
    title: "دراسة عن مقاومة الحديد للتآكل",
    summary: "دراسة حديثة حول تقنيات تحسين مقاومة الحديد للتآكل وزيادة عمره الافتراضي",
    date: "2023-08-10",
    category: "studies",
    imageUrl: "/lovable-uploads/ec6b9a95-c92f-4cb9-b6d0-4206b0c9c183.png"
  },
  {
    id: 4,
    title: "ملتقى الفنون للصناعة الحديثة",
    summary: "تغطية لفعاليات ملتقى الفنون للصناعة الحديثة وأبرز المتحدثين والمواضيع المطروحة",
    date: "2023-11-05",
    category: "events",
    imageUrl: "/lovable-uploads/6f61eeea-613a-4d0f-93ea-337425960036.png"
  },
  {
    id: 5,
    title: "صناعة الزجاج المعالج حراريًا",
    summary: "تعرف على خصائص الزجاج المعالج حراريًا واستخداماته في المباني الحديثة",
    date: "2023-07-18",
    category: "educational",
    imageUrl: "/lovable-uploads/e97f392b-3dca-4d61-ab66-742676bbbdc5.png"
  },
  {
    id: 6,
    title: "أساليب التصميم الداخلي المعاصر",
    summary: "استعراض لأحدث أساليب التصميم الداخلي واستخدام المواد المختلفة فيها",
    date: "2023-09-30",
    category: "global",
    imageUrl: "/lovable-uploads/74a1661f-2cf0-4bad-93ef-c23b37d9777d.png"
  }
];

const getCategoryIcon = (category: string) => {
  switch (category) {
    case "educational":
      return <BookOpen className="w-4 h-4" />;
    case "events":
      return <Calendar className="w-4 h-4" />;
    case "exhibitions":
      return <Users className="w-4 h-4" />;
    case "studies":
      return <Award className="w-4 h-4" />;
    case "local":
    case "regional":
    case "global":
      return <Globe className="w-4 h-4" />;
    default:
      return <MapPin className="w-4 h-4" />;
  }
};

const getCategoryName = (category: string) => {
  switch (category) {
    case "educational":
      return "مقالات تعليمية";
    case "events":
      return "فعاليات";
    case "exhibitions":
      return "معارض";
    case "studies":
      return "دراسات";
    case "local":
      return "أخبار محلية";
    case "regional":
      return "أخبار إقليمية";
    case "global":
      return "أخبار عالمية";
    default:
      return "أخرى";
  }
};

const ArticlesPage = () => {
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null);

  const filteredArticles = selectedCategory 
    ? articlesData.filter(article => article.category === selectedCategory)
    : articlesData;

  return (
    <div className="min-h-screen" dir="rtl">
      <Navbar />
      <div className="container mx-auto py-8">
      <div className="text-center mb-10">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold text-primary mb-4"
        >
          مقالات مفيدة
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-lg text-gray-600"
        >
          مقالات تعليمية، فعاليات، معارض، دراسات ومناسبات محلية وإقليمية وعالمية
        </motion.p>
      </div>

      {/* فلتر التصنيفات */}
      <div className="mb-8 flex flex-wrap justify-center gap-2">
        <Button 
          onClick={() => setSelectedCategory(null)} 
          variant={selectedCategory === null ? "default" : "outline"}
          className="text-sm"
        >
          الكل
        </Button>
        <Button 
          onClick={() => setSelectedCategory("educational")} 
          variant={selectedCategory === "educational" ? "default" : "outline"}
          className="text-sm"
        >
          <BookOpen className="ml-1 w-4 h-4" /> مقالات تعليمية
        </Button>
        <Button 
          onClick={() => setSelectedCategory("events")} 
          variant={selectedCategory === "events" ? "default" : "outline"}
          className="text-sm"
        >
          <Calendar className="ml-1 w-4 h-4" /> فعاليات
        </Button>
        <Button 
          onClick={() => setSelectedCategory("exhibitions")} 
          variant={selectedCategory === "exhibitions" ? "default" : "outline"}
          className="text-sm"
        >
          <Users className="ml-1 w-4 h-4" /> معارض
        </Button>
        <Button 
          onClick={() => setSelectedCategory("studies")} 
          variant={selectedCategory === "studies" ? "default" : "outline"}
          className="text-sm"
        >
          <Award className="ml-1 w-4 h-4" /> دراسات
        </Button>
        <Button 
          onClick={() => setSelectedCategory("global")} 
          variant={selectedCategory === "global" ? "default" : "outline"}
          className="text-sm"
        >
          <Globe className="ml-1 w-4 h-4" /> مناسبات عالمية
        </Button>
      </div>

      {/* عرض المقالات */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredArticles.map(article => (
          <motion.div
            key={article.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-lg shadow-md overflow-hidden"
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                onError={(e) => {
                  e.currentTarget.src = "/placeholder.svg";
                }}
              />
              <div className="absolute top-2 right-2 bg-primary text-white px-2 py-1 rounded-md text-xs font-medium flex items-center">
                {getCategoryIcon(article.category)}
                <span className="mr-1">{getCategoryName(article.category)}</span>
              </div>
            </div>
            <div className="p-4">
              <h3 className="text-xl font-bold mb-2">{article.title}</h3>
              <p className="text-gray-600 mb-3 text-sm">{article.summary}</p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  <Calendar className="inline mr-1 h-4 w-4" />
                  {new Date(article.date).toLocaleDateString('ar-SA')}
                </span>
                <Button size="sm" variant="link" asChild>
                  <Link to={`/articles/${article.id}`}>قراءة المزيد</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredArticles.length === 0 && (
        <div className="text-center py-10">
          <p className="text-xl text-gray-500">لا توجد مقالات في هذا التصنيف</p>
        </div>
      )}
      </div>
      <Footer />
    </div>
  );
};

export default ArticlesPage;
